import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 15s default timeout protects every page (dashboard, polling) from a hung
// FastAPI worker — without this an in-progress AI Diagnostic could blackout
// the dashboard waiting for a response that never comes.
const api = axios.create({ baseURL: API_BASE, timeout: 15000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("sv_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("sv_token");
      localStorage.removeItem("sv_user");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  register: (data) => api.post("/auth/register", data),
  me: () => api.get("/auth/me"),
};

export const adminApi = {
  clients: () => api.get("/admin/clients"),
  createClient: (data) => api.post("/admin/clients", data),
  updateClient: (orgId, data) => api.put(`/admin/clients/${orgId}`, data),
  clientHealth: (orgId) => api.get(`/admin/clients/${orgId}/health`),
  systemHealth: () => api.get("/admin/system-health"),
};

export const dashboardApi = {
  summary: (config) => api.get("/dashboard/summary", config),
  financialReality: (config) => api.get("/dashboard/financial-reality", config),
  applyAssessedData: () => api.post("/dashboard/apply-assessed-data"),
  sentinelInsights: (config) => api.get("/dashboard/sentinel-insights", config),
  sentinelSummary: (config) => api.get("/dashboard/sentinel-summary", config),
  sentinelScores: (config) => api.get("/dashboard/sentinel-scores", config),
  vaultTotalsByCurrency: (config) =>
    api.get("/dashboard/vault-totals-by-currency", config),
};

export const vaultApi = {
  list: () => api.get("/vaults"),
  get: (id) => api.get(`/vaults/${id}`),
  history: (id) => api.get(`/vaults/${id}/history`),
  transfer: (data) => api.post("/vaults/transfer", data),
  manualAdjust: (data) => api.post("/vaults/manual-adjust", data),
  ledger: (vaultType) => api.get(vaultType ? `/vault-ledger?vault_type=${vaultType}` : "/vault-ledger"),
};

export const bubbleApi = {
  list: () => api.get("/bubbles"),
  get: (id) => api.get(`/bubbles/${id}`),
};

export const obligationApi = {
  list: () => api.get("/obligations"),
  schedulePayments: () => api.post("/obligations/schedule-payments"),
};

export const behaviourApi = {
  metrics: () => api.get("/behaviour/metrics"),
  state: () => api.get("/behaviour/state"),
  coachingFeed: () => api.get("/behaviour/coaching-feed"),
};

export const guardrailApi = {
  previewSpend: (data) => api.post("/guardrails/preview-spend", data),
  createOverride: (data) => api.post("/overrides", data),
  listOverrides: () => api.get("/overrides"),
};

export const eventApi = {
  simulate: (data) => api.post("/events/simulate", data),
};

export const auditApi = {
  list: () => api.get("/audit"),
};

export const bankApi = {
  providers: () => api.get("/bank/providers"),
  connections: () => api.get("/bank/connections/v2"),
  connect: (provider) => api.post(`/bank/connect/${provider}`),
  accounts: (connId) => api.get(`/bank/connections/${connId}/accounts`),
  sync: (connId) => api.post(`/bank/connections/${connId}/sync`),
  disconnect: (connId) => api.post(`/bank/connections/${connId}/disconnect`),
  toggleAutoSync: (connId) => api.post(`/bank/connections/${connId}/auto-sync`),
  syncRuns: () => api.get("/bank/sync-runs"),
  feedToPool: (connId) => api.post(`/bank/feed-to-pool/${connId}`),
  transactions: () => api.get("/bank/transactions"),
  autoMatch: () => api.post("/matching/auto-match"),
  manualMatch: (data) => api.post("/matching/manual", data),
};

export const aiApi = {
  explain: (data) => api.post("/ai/explain", data),
  coach: () => api.post("/ai/coach"),
  simulate: (data) => api.post("/ai/simulate", data),
};

export const behaviourLiveApi = {
  compute: () => api.post("/behaviour/compute"),
};

export const auditChainApi = {
  chain: () => api.get("/audit/chain"),
};

export const financeApi = {
  types: () => api.get("/finance/types"),
  expense: (data) => api.post("/finance/expense", data),
  depositReceived: (data) => api.post("/finance/deposit/received", data),
  depositPaid: (data) => api.post("/finance/deposit/paid", data),
  releaseDeposit: (id) => api.post(`/finance/deposit/${id}/release`),
  deposits: () => api.get("/finance/deposits"),
  capital: (data) => api.post("/finance/capital", data),
  dividend: (data) => api.post("/finance/dividend", data),
  adjustment: (data) => api.post("/finance/adjustment", data),
  adjustments: () => api.get("/finance/adjustments"),
  commitment: (data) => api.post("/finance/commitment", data),
  cancelCommitment: (id) => api.post(`/finance/commitment/${id}/cancel`),
  commitments: () => api.get("/finance/commitments"),
  split: (data) => api.post("/finance/split", data),
  vaultAdjust: (data) => api.post("/finance/vault-adjust", data),
  vaultAdjustments: () => api.get("/finance/vault-adjustments"),
  events: () => api.get("/finance/events"),
};

export const recoveryApi = {
  risks: () => api.get("/recovery/risks"),
  simulate: (actionType) => api.post(`/recovery/simulate/${actionType}`),
  coaching: () => api.get("/recovery/coaching"),
};

export const reportApi = {
  data: () => api.get("/report/truth/data"),
  downloadPdf: () => api.get("/report/truth", { responseType: "blob" }),
};

export const ingestApi = {
  csvPreview: (file) => { const fd = new FormData(); fd.append("file", file); return api.post("/ingest/csv/preview", fd); },
  csvUpload: (file, mapping) => { const fd = new FormData(); fd.append("file", file); fd.append("mapping", JSON.stringify(mapping)); return api.post("/ingest/csv", fd); },
  createInvoice: (data) => api.post("/ingest/invoice", data),
  createBill: (data) => api.post("/ingest/bill", data),
  invoices: () => api.get("/ingest/invoices"),
  bills: () => api.get("/ingest/bills"),
};

export const traceApi = {
  trace: (type, id) => api.get(`/trace/${type}/${id}`),
};

export const dataPoolApi = {
  get: (params = {}) => {
    // Accept legacy string (uploadId) or object { batchId, uploadId, runId, scope }
    if (typeof params === "string") {
      return api.get(params ? `/data-pool?upload_id=${params}` : "/data-pool");
    }
    const qs = [];
    if (params.batchId) qs.push(`batch_id=${encodeURIComponent(params.batchId)}`);
    if (params.uploadId) qs.push(`upload_id=${encodeURIComponent(params.uploadId)}`);
    if (params.runId) qs.push(`run_id=${encodeURIComponent(params.runId)}`);
    if (params.scope) qs.push(`scope=${encodeURIComponent(params.scope)}`);
    return api.get(qs.length ? `/data-pool?${qs.join("&")}` : "/data-pool");
  },
  upload: (files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    return api.post("/data-pool/upload", fd);
  },
  getByUpload: (uploadId) => api.get(`/data-pool/by-upload/${uploadId}`),
  getUploads: () => api.get("/data-pool/uploads"),
  getBatchStatus: (uploadId) => api.get(`/import/batch/${uploadId}/status`),
  addUpload: (uploadId) => api.post(`/data-pool/add/${uploadId}`),
  study: () => api.post("/data-pool/study"),
  studyStatus: (runId) => api.get(`/data-pool/study-status/${runId}`),
  approveItem: (id) => api.post(`/data-pool/items/${id}/approve`),
  classifyItem: (id, data) => api.post(`/data-pool/items/${id}/classify`, data),
  allocateItem: (id, data) => api.post(`/data-pool/items/${id}/allocate`, data),
  editItem: (id, data) => api.put(`/data-pool/items/${id}/edit`, data),
  recalculateItem: (id) => api.post(`/data-pool/items/${id}/recalculate`),
  itemHistory: (id) => api.get(`/data-pool/items/${id}/history`),
  approveAllSafe: () => api.post("/data-pool/approve-all"),
  approveSelected: () => api.post("/data-pool/approve-selected"),
  commit: () => api.post("/data-pool/commit"),
};

export const reportsApi = {
  generateTruthReport: () => api.post("/reports/financial-truth/generate"),
  getTruthReport: (id) => api.get(`/reports/financial-truth/${id}`),
  getLatestTruthReport: () => api.get("/reports/financial-truth/latest"),
  downloadTruthReport: (id, format) => api.get(`/reports/financial-truth/${id}/download?format=${format}`, { responseType: "blob" }),
};

export const diagnosticApi = {
  run: () => api.post("/ai-diagnostic/run"),
  runs: () => api.get("/ai-diagnostic/runs"),
  getRun: (id) => api.get(`/ai-diagnostic/run/${id}`),
  getReport: (id) => api.get(`/ai-diagnostic/run/${id}/report`),
  exportPdf: (id) => api.get(`/ai-diagnostic/run/${id}/export/pdf`, { responseType: "blob" }),
  exportCsv: (id) => api.get(`/ai-diagnostic/run/${id}/export/csv`, { responseType: "blob" }),
};

export const sentinelApi = {
  chat: (message, session_id) => api.post("/sentinel/chat", { message, session_id }),
  history: () => api.get("/sentinel/history"),
  insights: () => api.post("/sentinel/insights"),
};

export const orgApi = {
  resetAllData: (confirmationText) => api.post("/org/reset-all-data", { confirmation_text: confirmationText }),
  resetDerivedTruth: (confirmationText) => api.post("/org/reset-derived-truth", { confirmation_text: confirmationText }),
};

export const demoApi = {
  start: () => api.post("/demo/start"),
  reset: () => api.post("/demo/reset"),
};

export default api;
